import { useEffect, useRef, useState } from 'react'

export const useStickyHeight = (
  mobile: boolean,
  hasShopCartSticky?: boolean
) => {
  const stickyRef = useRef<HTMLDivElement | null>(null)
  const stickyShoppingCartExtraBufferHeight = hasShopCartSticky ? 50 : 0 // Adjust height if shop cart sticky is present
  const fallbackHeight = 80 + stickyShoppingCartExtraBufferHeight
  const [stickyHeight, setStickyHeight] = useState<number>(fallbackHeight)

  /*
   * This effect runs as a fallback to handle setting the sticky height on first load
   */
  useEffect(() => {
    setStickyHeight(
      stickyRef.current
        ? stickyRef.current.offsetHeight + stickyShoppingCartExtraBufferHeight
        : fallbackHeight
    )
  }, [
    stickyRef.current?.offsetHeight,
    fallbackHeight,
    stickyShoppingCartExtraBufferHeight
  ])

  useEffect(() => {
    const stickyElement = stickyRef.current

    const updateStickyHeight = () => {
      if (mobile) {
        const newHeight = stickyElement
          ? stickyElement.offsetHeight + stickyShoppingCartExtraBufferHeight
          : fallbackHeight

        setStickyHeight(newHeight)
      }
    }

    if (!window.ResizeObserver) {
      // eslint-disable-next-line no-console
      console.warn(
        'ResizeObserver not supported. Using window resize event as fallback'
      )

      const handleResize = () => {
        setTimeout(updateStickyHeight, 100)
      }

      handleResize()

      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }

    let observer: ResizeObserver | null = null

    try {
      observer = new ResizeObserver(updateStickyHeight)
      if (stickyElement) {
        observer.observe(stickyElement)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('ResizeObserver failed', error)
    }

    return () => {
      if (observer && stickyElement) {
        observer.disconnect()
      }
    }
  }, [fallbackHeight, mobile, stickyShoppingCartExtraBufferHeight])

  // Adds scroll padding when elements snap into view for accessibility sake
  useEffect(() => {
    if (stickyHeight && stickyRef.current) {
      document.documentElement.style.scrollPaddingBlockEnd = `${stickyHeight}px`
    }
  }, [stickyHeight])

  return { stickyRef, stickyHeight }
}
